import { type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import moment from 'moment';
import { isGranted } from 'helpers/helpers';
import roles from 'helpers/roles';
import RegularButton from 'components/CustomButtons/Button.jsx';
import CustomInput from 'components/CustomInput/CustomInput';
import LabeledCheckbox from 'components/Checkbox/LabeledCheckbox';
import SelectInputBase from 'components/FormSelect/SelectInput';
import GridContainer from 'components/Grid/GridContainer.jsx';
import GridItem from 'components/Grid/GridItem.jsx';

import {
  createRangeOptions,
  getDirectionsForEventType,
  hasUnsavedChanges,
  joinOffset,
  MAX_OFFSET_DAYS,
  splitOffset,
  withCurrentValueOption,
  type CommunicationDictionary,
  type OffsetParts,
  type SelectOption,
  type CommunicationRuleRow,
} from './communicationRulesHelpers';

import 'views/Variants/components/styles.css';

const SelectInput = SelectInputBase as unknown as ComponentType<{
  label?: string;
  options: SelectOption[];
  mapBy: string;
  trackBy: string;
  value: string;
  disabled: boolean;
  handleChange: (event: { target: { value: string } }) => void;
}>;

type CommunicationRulesTableProps = {
  rows: CommunicationRuleRow[];
  dictionary: CommunicationDictionary;
  onAddRow: () => void;
  onChangeEventType: (key: string, eventType: string) => void;
  onChangeRow: (key: string, changes: Partial<CommunicationRuleRow>) => void;
  onToggleActive: (key: string) => void;
  savingRowKeys: string[];
  onSaveRow: (row: CommunicationRuleRow) => void;
  onRemoveRow: (key: string) => void;
};

const COLUMNS_COUNT = 8;

const OFFSET_PARTS: { part: keyof OffsetParts; max: number }[] = [
  { part: 'days', max: MAX_OFFSET_DAYS },
  { part: 'hours', max: 23 },
  { part: 'minutes', max: 59 },
];

const CommunicationRulesTable = ({
  rows,
  dictionary,
  onAddRow,
  onChangeEventType,
  onChangeRow,
  onToggleActive,
  savingRowKeys,
  onSaveRow,
  onRemoveRow,
}: CommunicationRulesTableProps) => {
  const { t, i18n } = useTranslation();

  const translateCode = (translationKey: string, code: string) =>
    i18n.exists(translationKey) ? t(translationKey) : code;

  const canEdit = isGranted(roles.ROLE_EDIT_COMMUNICATION_RULE);
  const canCreate = isGranted(roles.ROLE_CREATE_COMMUNICATION_RULE);
  const canRemove = isGranted(roles.ROLE_REMOVE_COMMUNICATION_RULE);

  const eventTypeOptions = dictionary.eventTypes.map(eventType => ({
    value: eventType,
    label: translateCode(
      `communicationRules.eventType.${eventType}`,
      eventType
    ),
  }));

  const channelOptions = dictionary.channels.map(channel => ({
    value: channel,
    label: translateCode(`communicationRules.channel.${channel}`, channel),
  }));

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>{t('communicationRules.column.eventType')}</TableCell>
          <TableCell>{t('communicationRules.column.direction')}</TableCell>
          <TableCell>{t('communicationRules.column.offset')}</TableCell>
          <TableCell>{t('communicationRules.column.channel')}</TableCell>
          <TableCell>{t('communicationRules.column.active')}</TableCell>
          <TableCell>{t('communicationRules.column.template')}</TableCell>
          <TableCell>{t('communicationRules.column.lastScannedAt')}</TableCell>
          {(canEdit || canCreate || canRemove) && (
            <TableCell>{t('notify.actions')}</TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(row => {
          const isPersisted = Boolean(row['@id']);
          const offsetParts = splitOffset(row.offsetMinutes);
          const directionOptions = getDirectionsForEventType(
            dictionary.eventTypeDefinitions,
            row.eventType
          ).map(direction => ({
            value: direction,
            label: t(`communicationRules.direction.${direction}`),
          }));

          return (
            <TableRow key={row.key}>
              <TableCell width="14%">
                <SelectInput
                  options={withCurrentValueOption(
                    eventTypeOptions,
                    row.eventType
                  )}
                  mapBy="label"
                  trackBy="value"
                  value={row.eventType}
                  disabled={isPersisted}
                  handleChange={(event: { target: { value: string } }) =>
                    onChangeEventType(row.key, event.target.value)
                  }
                />
              </TableCell>
              <TableCell width="11%">
                <SelectInput
                  options={directionOptions}
                  mapBy="label"
                  trackBy="value"
                  value={row.direction}
                  disabled={isPersisted || directionOptions.length < 2}
                  handleChange={(event: { target: { value: string } }) =>
                    onChangeRow(row.key, {
                      direction: event.target.value as 'BEFORE' | 'AFTER',
                    })
                  }
                />
              </TableCell>
              <TableCell width="17%">
                <GridContainer>
                  {OFFSET_PARTS.map(({ part, max }) => (
                    <GridItem key={part} xs={4}>
                      <SelectInput
                        label={t(`communicationRules.offset.${part}`)}
                        options={createRangeOptions(max, offsetParts[part])}
                        mapBy="label"
                        trackBy="value"
                        value={String(offsetParts[part])}
                        disabled={isPersisted}
                        handleChange={(event: { target: { value: string } }) =>
                          onChangeRow(row.key, {
                            offsetMinutes: joinOffset({
                              ...offsetParts,
                              [part]: Number(event.target.value),
                            }),
                          })
                        }
                      />
                    </GridItem>
                  ))}
                </GridContainer>
              </TableCell>
              <TableCell width="14%">
                <SelectInput
                  options={withCurrentValueOption(channelOptions, row.channel)}
                  mapBy="label"
                  trackBy="value"
                  value={row.channel}
                  disabled={isPersisted}
                  handleChange={(event: { target: { value: string } }) =>
                    onChangeRow(row.key, { channel: event.target.value })
                  }
                />
              </TableCell>
              <TableCell width="6%">
                <LabeledCheckbox
                  label=""
                  checked={row.active}
                  disabled={isPersisted && !canEdit}
                  onChange={() => onToggleActive(row.key)}
                />
              </TableCell>
              <TableCell width="18%">
                <CustomInput
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    disabled: isPersisted && !canEdit,
                    multiline: true,
                    rowsMax: 4,
                    value: row.template,
                    onChange: (event: { target: { value: string } }) =>
                      onChangeRow(row.key, { template: event.target.value }),
                  }}
                />
              </TableCell>
              <TableCell width="9%">
                {row.lastScannedAt
                  ? moment(row.lastScannedAt).format('YYYY-MM-DD HH:mm')
                  : '—'}
              </TableCell>
              {(canEdit || canCreate || canRemove) && (
                <TableCell>
                  {(isPersisted ? canEdit : canCreate) && (
                    <RegularButton
                      fullWidth={true}
                      disabled={
                        !hasUnsavedChanges(row) ||
                        savingRowKeys.includes(row.key)
                      }
                      color="success"
                      onClick={() => onSaveRow(row)}
                    >
                      {t('form.save')}
                    </RegularButton>
                  )}
                  {(!isPersisted || canRemove) && (
                    <RegularButton
                      fullWidth={true}
                      color="danger"
                      onClick={() => onRemoveRow(row.key)}
                    >
                      {t('form.delete')}
                    </RegularButton>
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        })}
        {canCreate && (
          <TableRow>
            <TableCell
              className="variant-addRow"
              colSpan={COLUMNS_COUNT}
              align="center"
              onClick={onAddRow}
            >
              {t('communicationRules.addRule')}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default CommunicationRulesTable;
